import { Skeleton } from '@/components/ui/skeleton';
import { useInventoryDrones } from '@/lib/api/queries';
import type { DjiStatus, InventoryDrone } from '@/lib/api/types';
import { config } from '@/lib/config';
import { cn } from '@/lib/utils';
import { RefreshCcw, Search, X } from 'lucide-react';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { type DroneFilterKey, useInvStore } from './InvStore';
import { SyncDialog } from './SyncDialog';
import { djiOnline, dockRow, linkLabel, linkLight, telemetryLight } from './djiStatus';
import { filterInventoryDrones, matchesFilters } from './droneSearch';
import { useDebouncedValue } from './hooks/useDebouncedValue';
import { useNow } from './hooks/useNow';
import { initialVisibleCount, nextVisibleCount } from './pagination';

type AgentState = 'online' | 'offline' | 'none';

/** Personality pill colours from the mockup (`.pers-pill[data-p]`). */
// Theme tokens, not hex: these are used as the badge's *text* colour over a
// 12%-alpha tint of themselves, so a fixed light-mode hue lands at ~2:1 on the
// dark surface. The tokens flip with the theme; the literals could not.
const PERS_COLOR: Record<string, string> = {
  MAVLink: 'var(--color-blue)',
  DJI: 'var(--color-amber)',
  'ADS-B Out': 'var(--color-purple)',
};

/** Adapters without a mockup colour (new or unknown types) get a neutral tone. */
const persColor = (label: string): string => PERS_COLOR[label] ?? 'var(--color-text2)';

const PAGE_SIZE = 12;
// "Load more" fires once the user has scrolled through about 2/3 of the
// current page, so the next chunk is ready before they hit the bottom.
const TRIGGER_OFFSET = Math.ceil(PAGE_SIZE / 3);

export function DronesPanel() {
  const { data, isPending, isError, refetch, isFetching } = useInventoryDrones();
  const filters = useInvStore((s) => s.droneFilters);
  const search = useInvStore((s) => s.droneSearch);
  const setSearch = useInvStore((s) => s.setDroneSearch);
  const selected = useInvStore((s) => s.selectedDroneId);
  const select = useInvStore((s) => s.selectDrone);
  const [syncOpen, setSyncOpen] = useState(false);
  const debouncedSearch = useDebouncedValue(search, 300);

  const all = data ?? [];
  // Re-evaluate DJI telemetry freshness (filters, header, card lights) between
  // refetches — only ticks when the fleet has a DJI aircraft.
  const now = useNow(
    5_000,
    all.some((d) => d.dji_status != null),
  );
  // Custodian has no server-side search, so this runs entirely client-side
  // over the already-fetched fleet — chip filters (AND), then a fuzzy match.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `now` re-runs the DJI telemetry-age filter
  const filtered = useMemo(
    () => filterInventoryDrones(all, filters, debouncedSearch),
    [all, filters, debouncedSearch, now],
  );
  // Distinguishes the two empty-result causes below: if the chip filters
  // alone already exclude every drone, that's the reason — even with a
  // search term typed in — rather than blaming the search.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `now` re-runs the DJI telemetry-age filter
  const anyMatchChipFiltersAlone = useMemo(
    () => all.some((d) => matchesFilters(d, filters)),
    [all, filters, now],
  );

  // Header rollup is data-source-aware: the Custodian API has no airworthiness/
  // maintenance concept (only connectivity + retirement), so live mode shows
  // custodian online/offline/retired; mock keeps airworthy/maintenance/no-agent.
  const live = !config.isMock;
  const online = all.filter((d) =>
    d.dji_status ? djiOnline(d) : d.custodian_status === 'online',
  ).length;
  const retired = all.filter((d) => d.status === 'Decommissioned').length;
  const offline = all.length - online - retired;
  const airworthy = all.filter((d) => d.status === 'Airworthy' && d.agent_deployed).length;
  const maint = all.filter((d) => d.status === 'Maintenance').length;
  const noAgent = all.filter((d) => !d.agent_deployed).length;

  // Infinite scroll over the already-fully-loaded, already-filtered array —
  // Custodian returns the whole fleet in one call, so there's no real network
  // page to fetch; this just windows how much of it is rendered.
  const [visibleCount, setVisibleCount] = useState(() =>
    initialVisibleCount(filtered.length, PAGE_SIZE),
  );
  const visible = filtered.slice(0, visibleCount);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Reset to the first page whenever the filtered set changes (search/filters).
  // Keyed on the ids, not `filtered`'s reference or length — two result sets can
  // have the same size (switching search terms), but a background refetch or the
  // DJI freshness tick hands back a new array with the SAME drones, and
  // resetting then would shrink the list and jump the scroll position.
  const filteredKey = useMemo(() => filtered.map((d) => d.drone_id).join('|'), [filtered]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `filteredKey` is the change signal
  useEffect(() => {
    setVisibleCount(initialVisibleCount(filtered.length, PAGE_SIZE));
  }, [filteredKey]);

  // The trigger wraps a different card each time `visibleCount` changes (see
  // `triggerIndex` below), so the observer must reattach to the new DOM node.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `visibleCount` is load-bearing, not redundant.
  useEffect(() => {
    const el = triggerRef.current;
    const root = containerRef.current;
    if (!el || !root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisibleCount((c) => nextVisibleCount(c, PAGE_SIZE, filtered.length));
        }
      },
      { root },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [filtered.length, visibleCount]);

  const triggerIndex = Math.max(0, visibleCount - TRIGGER_OFFSET);

  // Pre-select the first visible drone so the detail pane is populated on entry.
  useEffect(() => {
    if (!selected && filtered.length > 0) select(filtered[0]!.drone_id);
  }, [filtered, selected, select]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <header className="px-3 py-2.5 border-b border-(--color-border) shrink-0 flex items-center gap-2">
        <span className="text-[0.923em] font-bold">Drones</span>
        <span className="text-[0.692em] text-(--color-text3) font-mono truncate mr-auto">
          {live ? (
            <>
              {all.length} aircraft · <span className="text-(--color-green)">{online} online</span>{' '}
              · <span className="text-(--color-text2)">{offline} offline</span>
              {retired > 0 && (
                <>
                  {' '}
                  · <span className="text-(--color-text3)">{retired} retired</span>
                </>
              )}
            </>
          ) : (
            <>
              {all.length} aircraft ·{' '}
              <span className="text-(--color-green)">{airworthy} airworthy</span> ·{' '}
              <span className="text-(--color-amber)">{maint} maintenance</span> ·{' '}
              <span className="text-(--color-red)">{noAgent} no agent</span>
            </>
          )}
        </span>
        <button
          type="button"
          onClick={() => setSyncOpen(true)}
          title="Sync drones from DroneLogBook"
          className="h-6 px-2.5 rounded-full bg-(--color-green) text-(--color-on-accent) text-[0.692em] font-bold flex items-center gap-1 shrink-0"
        >
          <RefreshCcw size={11} className={cn(isFetching && 'animate-spin')} />{' '}
          {isFetching ? 'Syncing…' : 'Sync'}
        </button>
      </header>

      <div ref={containerRef} className="flex-1 overflow-y-auto p-3">
        <div className="relative mb-2">
          <Search
            size={12}
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 opacity-40"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search drones"
            placeholder="Search drones…"
            className="w-full rounded-md border border-(--color-border) bg-(--color-bg) py-[5px] pl-7 pr-7 text-[0.769em] text-(--color-text) outline-none focus:border-(--color-green)"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label="Clear search"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 cursor-pointer rounded p-0.5 text-(--color-text3) hover:bg-(--color-surface) hover:text-(--color-text)"
            >
              <X size={12} />
            </button>
          )}
        </div>

        <FilterStack />

        {isPending && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {['s0', 's1', 's2', 's3'].map((k) => (
              <Skeleton key={k} className="h-[132px] w-full" />
            ))}
          </div>
        )}
        {isError && (
          <div className="text-[0.769em] text-(--color-red) text-center p-4 space-y-2">
            <div>Failed to load drones.</div>
            <button
              type="button"
              onClick={() => refetch()}
              className="text-(--color-green) underline"
            >
              Retry
            </button>
          </div>
        )}
        {!isPending && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {visible.map((d, i) => (
              <DroneCard
                key={d.drone_id}
                ref={i === triggerIndex ? triggerRef : undefined}
                d={d}
                selected={selected === d.drone_id}
                onClick={() => select(d.drone_id)}
              />
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full text-center text-[0.769em] text-(--color-text3) py-8">
                {anyMatchChipFiltersAlone
                  ? 'No drones match your search.'
                  : 'No drones match the active filters.'}
              </div>
            )}
          </div>
        )}
      </div>

      {filtered.length > 0 && (
        <footer className="shrink-0 px-3 py-2 border-t border-(--color-border) bg-(--color-surface)">
          <span className="text-[0.692em] font-mono text-(--color-text3)">
            {visible.length} of {filtered.length} loaded
          </span>
        </footer>
      )}

      <SyncDialog open={syncOpen} onOpenChange={setSyncOpen} type="drones" />
    </div>
  );
}

/* ── Filter stack — 5 axes (Adapter / Protocol / Custodian / State / dTwin) ── */

const FILTER_GROUPS: {
  label: string;
  chips: { key: DroneFilterKey; label: string; tone?: 'ok' | 'warn' | 'crit' }[];
}[] = [
  {
    label: 'Adapter',
    chips: [
      { key: 'adapter:MAVLink', label: 'MAVLink' },
      { key: 'adapter:DJI', label: 'DJI' },
      { key: 'adapter:ADS-B Out', label: 'ADS-B Out' },
      { key: 'adapter:Other', label: 'Other' },
    ],
  },
  {
    label: 'Protocol',
    chips: [{ key: 'protocol:MAVLink v2', label: 'MAVLink v2' }],
  },
  {
    label: 'Custodian',
    chips: [
      { key: 'custodian:aircraft', label: 'Deployed' },
      { key: 'custodian:gcs', label: 'GCS Plugin' },
      { key: 'custodian:none', label: 'None' },
    ],
  },
  {
    label: 'State',
    chips: [
      { key: 'state:online', label: 'Online', tone: 'ok' },
      { key: 'state:offline', label: 'Offline', tone: 'crit' },
    ],
  },
  {
    label: 'dTwin',
    chips: [
      { key: 'dtwin:updated', label: 'Synchronized', tone: 'ok' },
      { key: 'dtwin:outdated', label: 'Out of Sync', tone: 'warn' },
    ],
  },
];

function FilterStack() {
  const filters = useInvStore((s) => s.droneFilters);
  const toggle = useInvStore((s) => s.toggleDroneFilter);
  return (
    <div className="flex flex-col gap-1 pb-2 mb-2 border-b border-(--color-border-lite)">
      {FILTER_GROUPS.map((g) => (
        <div key={g.label} className="flex items-center flex-wrap gap-1">
          <span className="w-[78px] shrink-0 text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3)">
            {g.label}
          </span>
          {g.chips.map((c) => (
            <ChipSm key={c.key} on={filters[c.key]} tone={c.tone} onClick={() => toggle(c.key)}>
              {c.label}
            </ChipSm>
          ))}
        </div>
      ))}
    </div>
  );
}

function ChipSm({
  on,
  tone,
  children,
  onClick,
}: {
  on: boolean;
  tone?: 'ok' | 'warn' | 'crit';
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'h-[22px] px-2 rounded-full border text-[0.692em] font-medium transition-colors',
        on
          ? tone === 'ok'
            ? 'bg-(--color-green) border-(--color-green) text-(--color-on-accent)'
            : tone === 'warn'
              ? 'bg-(--color-amber) border-(--color-amber) text-(--color-on-accent)'
              : tone === 'crit'
                ? 'bg-(--color-red) border-(--color-red) text-(--color-on-accent)'
                : 'bg-(--color-selected) border-(--color-selected) text-(--color-selected-fg)'
          : 'bg-(--color-bg) border-(--color-border) text-(--color-text2) hover:border-(--color-green)',
      )}
    >
      {children}
    </button>
  );
}

/* ── Drone card ── */

const DroneCard = forwardRef<
  HTMLButtonElement,
  { d: InventoryDrone; selected: boolean; onClick: () => void }
>(function DroneCard({ d, selected, onClick }, ref) {
  const topBorder = !d.agent_deployed
    ? 'border-t-(--color-red)'
    : d.status === 'Maintenance'
      ? 'border-t-(--color-amber)'
      : 'border-t-(--color-green)';

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col gap-[7px] text-left w-full rounded-md border border-(--color-border) border-t-2 bg-(--color-surface) p-3.5 cursor-pointer transition-colors hover:border-(--color-green) hover:bg-(--color-green-bg)',
        topBorder,
        selected &&
          'border-(--color-green) bg-(--color-green-bg) outline outline-2 outline-(--color-green) -outline-offset-2',
      )}
    >
      <div className="flex items-center justify-between gap-1 min-w-0">
        {/* Callsign, now that the Custodian list carries it — the raw serial
            is still shown on the adapter row at the bottom of the card. */}
        <span className="text-[0.923em] font-bold font-mono truncate">{d.display_name}</span>
        <StatusBadge status={d.status} />
      </div>
      <div className="text-[0.731em] font-mono text-(--color-text2) truncate">
        {[d.brand, d.model, d.drone_type].filter(Boolean).join(' · ') || '—'}
      </div>

      <div className="flex flex-col gap-1 rounded border border-(--color-border-lite) bg-(--color-bg) px-2.5 py-[7px]">
        {d.dji_status ? (
          <DjiRows s={d.dji_status} />
        ) : (
          <>
            <AgentRow
              label="Custodian"
              state={d.custodian_status}
              text={agentText(d.custodian_status)}
            />
            <AgentRow
              label="GCS Plugin"
              state={d.gcs_plugin_status}
              text={agentText(d.gcs_plugin_status)}
            />
            <AgentRow
              label="dTwin"
              state={d.dtwin_sync}
              text={`${d.dtwin_sync === 'updated' ? 'Synchronized' : 'Out of Sync'} · ${d.dtwin_sync_label}`}
            />
          </>
        )}
      </div>

      <div className="flex items-center gap-1.5 text-[0.692em] font-mono text-(--color-text3) truncate">
        <span
          className="px-1.5 rounded-[3px] font-sans font-bold"
          style={{
            color: persColor(d.personality),
            // `${hex}20` no longer works now the value is a var() — mix the
            // token with transparent to get the same 12% tint in either theme.
            backgroundColor: `color-mix(in srgb, ${persColor(d.personality)} 14%, transparent)`,
          }}
        >
          {d.personality}
        </span>
        <span className="truncate">· {d.serial_number}</span>
      </div>
    </button>
  );
});

function DjiRows({ s }: { s: DjiStatus }) {
  const link = linkLight(s);
  const tel = telemetryLight(s);
  const dock = dockRow(s);
  return (
    <>
      <AgentRow label={linkLabel(s)} state={link.state} text={link.text} />
      <AgentRow label="Telemetry" state={tel.state} text={tel.text} />
      <AgentRow label="Dock" state={dock.state} text={dock.text} />
      {dock.sub && (
        <div className="pl-3 text-[0.692em] text-(--color-text3) truncate">{dock.sub}</div>
      )}
    </>
  );
}

function agentText(state: AgentState): string {
  return state === 'online' ? 'Online' : state === 'offline' ? 'Offline' : '—';
}

const DOT_CLASS: Record<AgentState | 'updated' | 'outdated', string> = {
  online:
    'bg-(--color-green) shadow-[0_0_0_2px_rgba(34,160,80,.22)] animate-[pulse-dot_2s_ease-in-out_infinite]',
  offline: 'bg-(--color-red)',
  none: 'bg-(--color-text3) opacity-35',
  updated: 'bg-(--color-green)',
  outdated: 'bg-(--color-amber)',
};

const STATE_TEXT_CLASS: Record<AgentState | 'updated' | 'outdated', string> = {
  online: 'text-(--color-green)',
  offline: 'text-(--color-red)',
  none: 'text-(--color-text3) italic font-medium',
  updated: 'text-(--color-green)',
  outdated: 'text-(--color-amber)',
};

function AgentRow({
  label,
  state,
  text,
}: {
  label: string;
  state: AgentState | 'updated' | 'outdated';
  text: string;
}) {
  return (
    <div className="flex items-center gap-1.5 text-[0.731em] font-medium text-(--color-text2)">
      <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', DOT_CLASS[state])} />
      {label}
      <span className={cn('ml-auto font-semibold text-[0.92em]', STATE_TEXT_CLASS[state])}>
        {text}
      </span>
    </div>
  );
}

function StatusBadge({ status }: { status: InventoryDrone['status'] }) {
  const tone =
    status === 'Airworthy'
      ? 'bg-(--color-green-bg) text-(--color-green)'
      : status === 'Maintenance'
        ? 'bg-(--color-amber-bg) text-(--color-amber)'
        : 'bg-(--color-red-bg) text-(--color-red)';
  return (
    <span className={cn('shrink-0 px-1.5 rounded text-[0.615em] font-bold', tone)}>{status}</span>
  );
}
